import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { INVITE_URL } from '../../config';
import { tryToInviteNewMember } from '../../store/brokers/configs/models/modelsSlice';
import Button from '../../shared/button';
import Input from '../../shared/input';
import Error from '../../shared/error';
import { useConfigFirstRunQuery } from '../../store/chatbox/chatboxApi';

interface IInviteFriendModal {
    openModal: boolean;
    handleCloseModal: () => void;
}

const InviteFriendModal = ({
    openModal,
    handleCloseModal,
}: IInviteFriendModal) => {
    const dispatch = useAppDispatch();
    const [emailInvite, setEmailInvite] = useState<string>();
    const [submitError, setSubmitError] = useState<string>();
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const { data: dataConfig, refetch } = useConfigFirstRunQuery();
    const successTimerRef = useRef<number | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const clearSuccessTimer = () => {
        if (successTimerRef.current) {
            window.clearTimeout(successTimerRef.current);
            successTimerRef.current = null;
        }
    };

    useEffect(() => {
        return () => clearSuccessTimer();
    }, []);

    const onCloseModal = () => {
        setEmailInvite(undefined);
        setSubmitError(undefined);
        clearSuccessTimer();
        setSuccessMsg(null);
        handleCloseModal();
    }

    const onChangeEmailInvite = (value: any) => {
        setEmailInvite(value)
    }

    const onEnterAction = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();

        const email = (emailInvite || '').trim();
        if (!email) {
            setSubmitError('Please enter an email address.');
            return;
        }

        try {
            setSubmitError(undefined);
            await dispatch(tryToInviteNewMember({ email, source: 'LEO_DASHBOARD' })).unwrap();
            setEmailInvite('');
            clearSuccessTimer();
            refetch();
            setSuccessMsg('Successfully sent invitation.');

            successTimerRef.current = window.setTimeout(() => setSuccessMsg(null), 7000);
        } catch (err) {
            clearSuccessTimer();
            setSuccessMsg(null);
            setSubmitError(String(err));
        }
    };

    useEffect(()=>{
        if(Number(dataConfig?.data?.inviteCount) === 5) {
            onCloseModal()
        }
    },[dataConfig?.data?.inviteCount])

    return (
        <div className={`fixed inset-0 z-20 ${openModal ? '' : 'pointer-events-none'}`}>
            <div
                className={`fixed inset-0 bg-black/50 dark:bg-gray-500/50 transition-opacity ${openModal ? 'opacity-100' : 'opacity-0'
                    }`}
                onClick={handleCloseModal}
            />

            <div
                className={`fixed inset-0 z-30 flex items-center justify-center transition ${openModal ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                    }`}
            >
                <div className='w-[95vw] md:w-[70vw] lg:w-[60vw] bg-white dark:bg-bgColor-darkMode shadow-lg rounded-xl max-h-[98dvh] overflow-y-auto'>
                    <div className='relative w-full'>
                        <div className='relative overflow-hidden rounded-xl'>
                            <div className='relative'>
                                <video
                                    ref={videoRef}
                                    className='absolute inset-0 w-full h-full object-cover'
                                    src='/video/white-background.mp4'
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    preload='auto'
                                />
                                <div className='relative px-6 py-2'>
                                    <div className='relative z-10 flex flex-col items-center gap-5 text-center'>
                                        <img
                                            src='/leo-agent.svg'
                                            alt='Logo'
                                            className='w-28 sm:w-40 md:w-44 object-contain'
                                            draggable={false}
                                        />

                                        <p className='text-[#00330F] text-2xl font-bold mb-1 mt-1'>
                                            Invite someone to see Claire
                                        </p>
                                        <p className='text-[#00330F] w-[80%] max-w-[30rem]'>
										Claire is invite only. Share with your close network before your invites expire.
                                        </p>
                                        <p className='text-[#00330F]'>You have {5 - Number(dataConfig?.data?.inviteCount || 0)}/5 invitations left.</p>

                                        <div className='my-3 w-full max-w-[20rem]'>
                                            <Input showValue inputValue={INVITE_URL} disabled />

                                            <Button
                                                icon={
                                                    <img
                                                        src='/assets/layout/share.svg'
                                                        className='w-[20px] object-contain mr-2'
                                                    />
                                                }
                                                label='Copy invitation link'
                                                onClickButton={() => navigator?.clipboard?.writeText(`${INVITE_URL}`)}
                                                className='btn-primary-rounded-less flex flex-row items-center !w-full justify-center !py-2 mt-3'
                                            />

                                            <p className='text-center my-2'>Or</p>

                                            <Input
                                                type='email'
                                                placeholder='Enter email address...'
                                                showValue
                                                inputValue={emailInvite}
                                                onChangeInput={onChangeEmailInvite}
                                                onHandleKeyDown={onEnterAction}
                                            />
                                            {successMsg && (
                                                <div className='mt-2 text-sm text-green-800'>
                                                    {successMsg}
                                                </div>
                                            )}
                                            {submitError && <Error text={submitError} />}

                                            <div className='h-[0.5px] w-full border border-[0.5px] border-black my-3' />

                                            <div
                                                onClick={onCloseModal}
                                                className='py-2 w-full text-center rounded-lg cursor-pointer hover:opacity-80 bg-white dark:bg-black'
                                            >
                                                <p className='underline text-[#00330F] dark:text-white'>Skip for now</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='pb-2' />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default InviteFriendModal;